import { Button, Checkbox, Form, Input, message, Modal, Select } from 'antd';
import React from 'react';
import s from './s.module.less';
import { useLicenseInfo } from 'hooks/newUniprofile/useLicenseInfo';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields } from 'types/enumerationData';
import { ServerMeta } from 'types/common';
import AutoAddressInput from 'components/AutoAddressInput';
import { REQUIRED_FIELD } from 'constants/common';
import StateSelect from 'components/form/StateSelect';
import { ColumnsType } from 'antd/es/table';
import CommonTable from 'components/CommonTable';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { TPracticeState } from 'types/textVisit';
import { savePracticeState } from 'api/textVisit';
import usePracticeStateList from 'hooks/usePracticeStateList';

type Props = {
    onFinish?: () => void;
    isSettingModal?: boolean;
    isInModal?: boolean;
};

const PracticeStateConfig = (props: Props) => {
    const { onFinish, isSettingModal, isInModal } = props;
    const [licenseInfo, __, fetchLicenseData] = useLicenseInfo();
    const [selectStates, setSelectStates] = React.useState<string[]>([]);
    const [allStates] = useGetSettingData(EnumFields.STATE);
    const [showAddAddressModal, setShowAddAddressModal] = React.useState<boolean>(false);
    const [addressForm] = Form.useForm<TPracticeState>();
    const [currentEditState, setCurrentEditState] = React.useState<ServerMeta>();
    const [currentEditRecord, setCurrentEditRecord] = React.useState<Partial<TPracticeState>>();
    const tableData = React.useRef<Partial<TPracticeState>[]>([]);
    const [, forceUpdate] = React.useState({});
    const [submitLoading, setSubmitLoading] = React.useState<boolean>(false);
    const [serverPracticeStateList, serverPracticeStateListLoading, fetchServerPracticeStateList] = usePracticeStateList();

    React.useEffect(() => {
        if (serverPracticeStateList && serverPracticeStateList.length > 0) {
            tableData.current = serverPracticeStateList.map((item) => {
                return {
                    ...item,
                };
            });
            setSelectStates(serverPracticeStateList.map((item) => {
                return item.state!;
            }));
        }
    }, [serverPracticeStateList]);

    React.useEffect(() => {
        if (licenseInfo) {
            const { practiceLicense } = licenseInfo;
            const stateFromLicense: string[] = [];
            practiceLicense?.forEach((item) => {
                if (item.state && !stateFromLicense.includes(item.state)) {
                    stateFromLicense.push(item.state as string);
                }
            });
            if (tableData.current.length === 0) {
                setSelectStates(stateFromLicense);
            }
        }
    }, [licenseInfo]);

    React.useEffect(() => {
        //todo
        if (selectStates.length === 0) {
            tableData.current = [];
            forceUpdate({});
            return;
        }
        const addStates: string[] = selectStates.filter((state) => {
            return !tableData.current.some((item) => {
                return item.state === state;
            });
        });
        const _tableData = tableData.current.filter((item) => {
            return selectStates.includes(item.state!);
        });
        addStates.forEach((state) => {
            _tableData.push({
                state,
            });
        });
        tableData.current = _tableData;
        forceUpdate({});
    }, [selectStates]);

    const handleAddAddress = async () => {
        try {
            const values = await addressForm.validateFields();
            const targetRecord = tableData.current.find((item) => {
                return item.state === values.state;
            });
            if (targetRecord) {
                targetRecord.practiceName = values.practiceName;
                targetRecord.address = values.address;
                targetRecord.addressLine2 = values.addressLine2;
                targetRecord.city = values.city;
                targetRecord.zipCode = values.zipCode;
                console.log(values);
                forceUpdate({});
                setShowAddAddressModal(false);
                addressForm.resetFields();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleReset = () => {
        if (serverPracticeStateList && serverPracticeStateList.length > 0) {
            tableData.current = serverPracticeStateList.map((item) => {
                return {
                    ...item,
                };
            });
            setSelectStates(serverPracticeStateList.map((item) => {
                return item.state!;
            }));
        } else {
            tableData.current = [];
            setSelectStates([]);
            forceUpdate({});
        }
    };

    const handleSave = async () => {
        try {
            // 验证每个 state 都必须添加地址
            const statesWithoutAddress = tableData.current.filter((item) => !item.address);

            if (statesWithoutAddress.length > 0) {
                const stateNames = statesWithoutAddress.map(item => item.state).join(', ');
                message.error(`Please add a practice address for: ${stateNames}`);
                return;
            }

            setSubmitLoading(true);
            const res = await savePracticeState(tableData.current);
            setSubmitLoading(false);
            if (!res?.error) {
                if (isSettingModal) {
                    message.success('Practice information saved successfully');
                    fetchServerPracticeStateList();
                    return;
                }
                onFinish?.();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSubmitLoading(false);
        }
    };

    const columns: ColumnsType<Partial<TPracticeState>> = [
        {
            title: 'STATE',
            dataIndex: 'state',
        },
        {
            title: 'PRACTICE INFORMATION',
            dataIndex: 'practiceName',
            render: (practiceName: string, record: Partial<TPracticeState>) => {
                const hasAddress = record?.address;
                if (hasAddress) {
                    return (
                        <div className={s.withAddress}>
                            <div className={s.addressWrap}>
                                <div className={s.name}>{practiceName}</div>
                                <div className={s.value}>{record.address} {record.addressLine2 ? `, ${record.addressLine2}` : ''}</div>
                                <div className={s.value}>{record.city}, {record.state} {record.zipCode}</div>
                            </div>
                            <Button
                                onClick={() => {
                                    const _targetState = (allStates as ServerMeta[])?.find((item: ServerMeta) => {
                                        return item.dictKey === record.state || item.content === record.state;
                                    });
                                    if (_targetState) {
                                        setCurrentEditState(_targetState);
                                    }
                                    setCurrentEditRecord(record);
                                    addressForm.resetFields();
                                    addressForm.setFieldValue('state', record.state);
                                    addressForm.setFieldValue('practiceName', record.practiceName);
                                    addressForm.setFieldValue('address', record.address);
                                    addressForm.setFieldValue('addressLine2', record.addressLine2);
                                    addressForm.setFieldValue('city', record.city);
                                    addressForm.setFieldValue('zipCode', record.zipCode);
                                    setShowAddAddressModal(true);
                                }}
                            >
                                Edit
                            </Button>
                        </div>
                    );
                }
                return (
                    <div className={s.addWrap}>
                        <Button
                            onClick={() => {
                                setShowAddAddressModal(true);
                                const _targetState = (allStates as ServerMeta[])?.find((item: ServerMeta) => {
                                    return item.dictKey === record.state || item.content === record.state;
                                });
                                if (_targetState) {
                                    setCurrentEditState(_targetState);
                                }
                                setCurrentEditRecord(record);
                                addressForm.resetFields();
                                addressForm.setFieldValue('state', record.state);
                            }}
                            className={s.btn}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7.9999 2.40002C8.44173 2.40002 8.7999 2.7582 8.7999 3.20002V7.20002H12.7999C13.2417 7.20002 13.5999 7.5582 13.5999 8.00002C13.5999 8.44185 13.2417 8.80002 12.7999 8.80002H8.7999V12.8C8.7999 13.2419 8.44173 13.6 7.9999 13.6C7.55808 13.6 7.1999 13.2419 7.1999 12.8V8.80002H3.1999C2.75807 8.80002 2.3999 8.44185 2.3999 8.00002C2.3999 7.5582 2.75807 7.20002 3.1999 7.20002L7.1999 7.20002V3.20002C7.1999 2.7582 7.55808 2.40002 7.9999 2.40002Z" fill="#6B7280" />
                            </svg>
                            Add
                        </Button>
                        {
                            record.enableEpcs === true && (
                                <span className={s.text}>Add practice address</span>
                            )
                        }
                    </div>
                );
            },
        },
        {
            title: 'ENABLE EPCS',
            dataIndex: 'enableEpcs',
            key: 'enableEpcs',
            render: (enabled: boolean, record: Partial<TPracticeState>) => {
                return (
                    <div className={s.enabledWrap}>
                        <Checkbox
                            checked={enabled}
                            onChange={(val: CheckboxChangeEvent) => {
                                const targetRecord = tableData.current.find((item) => {
                                    return item.state === record.state;
                                });
                                if (targetRecord) {
                                    targetRecord.enableEpcs = val.target.checked;
                                }
                                forceUpdate({});
                            }}
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <div
            className={`${s.wrap} ${isInModal ? s.wrapModal : ''}`}
        >
            <h3>
                {
                    isSettingModal ? 'Manage your practice information' : 'Set up your prescribing details for each state where you practice'
                }
            </h3>
            <div className={s.des}>
                {
                    isSettingModal ? <div>You can view, edit, or add new practice details. To delete a practice, please contact <a href="mailto:providersupport@helloklarity.com">providersupport@helloklarity.com</a>.</div> : 'Choose the states where you want to prescribe medications'
                }
            </div>
            <div className={s.stateSelect}>
                <div className={s.label}>
                    Select states
                    <b>*</b>
                </div>
                <Select
                    style={{ width: '400px' }}
                    mode="multiple"
                    maxTagCount={6}
                    value={selectStates}
                    onChange={(val) => {
                        setSelectStates(val);
                    }}
                    size="large"
                    optionLabelProp="value"
                >
                    {
                        (allStates || [])?.map((item: ServerMeta) => {
                            const disable = serverPracticeStateList?.some((_item: TPracticeState) => {
                                return _item.state === item.dictKey;
                            }) && isSettingModal;
                            return (
                                <Select.Option
                                    key={item.dictKey}
                                    value={item.dictKey}
                                    disabled={disable}
                                >
                                    {item.content}
                                </Select.Option>
                            );
                        })
                    }

                </Select>
            </div>
            <div className={s.tableWrap}>
                <CommonTable
                    columns={columns}
                    data={tableData.current}
                    pagination={false}
                    bordered
                    loading={serverPracticeStateListLoading}
                />
            </div>
            <div className={s.tips}>
                You can manage your practice information for prescriptions in <b>Settings</b> under the <b>Practice information</b> tab.
            </div>
            <div className={s.actionWrap}>
                {
                    !isSettingModal && (
                        <Button
                            className={s.btn}
                            onClick={handleSave}
                            type="primary"
                            loading={submitLoading}
                        >
                            Next
                        </Button>
                    )
                }
                {
                    isSettingModal && (
                        <>
                            <Button
                                className={s.btn}
                                onClick={handleSave}
                                type="primary"
                                loading={submitLoading}
                            >
                                Save
                            </Button>
                            <Button
                                style={{
                                    marginLeft: '12px',
                                }}
                                onClick={handleReset}
                            >
                                Reset
                            </Button>
                        </>
                    )
                }

            </div>
            <Modal
                open={showAddAddressModal}
                onCancel={() => setShowAddAddressModal(false)}
                title="Add practice address"
                footer={null}
                width={800}
                destroyOnClose
            >
                <div className={s.addressForm}>
                    <Form form={addressForm} layout="vertical">
                        <div className={s.row}>
                            <Form.Item
                                label="Practice name"
                                name="practiceName"
                                style={{
                                    width: '28%',
                                }}
                                rules={[{ required: true, message: REQUIRED_FIELD }]}
                            >
                                <Input size="large" className={s.input} />
                            </Form.Item>
                        </div>
                        <div className={s.row}>
                            <Form.Item
                                rules={[{ required: true, message: REQUIRED_FIELD }]}
                                label="Street address"
                                name="address"
                                className={s.input}
                                style={{
                                    width: '70%',
                                }}
                            >
                                <AutoAddressInput
                                    size="large"
                                    targetState={currentEditState}
                                    className={s.input}
                                    onStateChange={(val) => {
                                        const st = (allStates as ServerMeta[])?.find((item: ServerMeta) => {
                                            return item.dictKey === val || item.content === val;
                                        });
                                        if (st) {
                                            addressForm.setFieldValue('state', st.dictKey);
                                        }
                                    }}
                                    onCityChange={(val) => {
                                        addressForm.setFieldValue('city', val);
                                    }}
                                    onPostcodeChange={(val) => {
                                        addressForm.setFieldValue('zip', val);
                                    }}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Apt., Suite, Unit (optional)"
                                name="addressLine2"
                                style={{
                                    width: '28%',
                                }}
                            >
                                <Input size="large" className={s.input} />
                            </Form.Item>
                        </div>
                        <div className={s.row}>
                            <Form.Item
                                style={{ width: '31%' }}
                                label="City"
                                name="city"
                                rules={[{ required: true, message: REQUIRED_FIELD }]}
                            >
                                <Input size="large" className={s.input} />
                            </Form.Item>
                            <Form.Item
                                style={{ width: '31%' }}
                                label="Zip"
                                name="zipCode"
                                rules={[{ required: true, message: REQUIRED_FIELD }]}
                            >
                                <Input size="large" className={s.input} />
                            </Form.Item>
                            <Form.Item
                                style={{ width: '33%' }}
                                label="State"
                                name="state"
                            >
                                <StateSelect size="large" disabled className={s.input} />
                            </Form.Item>
                        </div>
                    </Form>
                    <div className={s.btnWrap}>
                        <Button
                            size="large"
                            type="primary"
                            style={{
                                marginRight: '16px',
                            }}
                            onClick={handleAddAddress}
                        >
                            Save
                        </Button>
                        <Button
                            size="large"
                            type="default"
                            onClick={() => {
                                setShowAddAddressModal(false);
                                addressForm.resetFields();
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default PracticeStateConfig;
